import { useMutation, useQuery } from '@apollo/client';
import {
    GET_CONTACT_AND_CONVERSATION_BY_ID,
    GET_CONTACT_BY_ID,
    GET_CONTACTS_WEIGHTED_SCORES,
    MARK_ACTION_AS_COMPLETED,
} from '@zaplify/graphql';
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ChannelType } from '../../../types/channel-type';
import { MessageType } from '../../../types/message-type';
import dayjs from 'dayjs';
import { useSdk } from '../../../sdk';
import utc from 'dayjs/plugin/utc';
import { useQuery as useQueryTan } from '@tanstack/react-query';
import { ConnectionStatus } from '@zaplify/campaigns';
import { DisabledPrimaryActionReason } from './disabled-primary-action-reason';
import { useMessageValidation } from './use-message-validation';
import { useMessageActions } from './use-message-actions';
import { useDraftMessagesV2 } from '../use-draft-messages-v2';
import { useAutoGenerateDraft } from './use-auto-generate-draft';

dayjs.extend(utc);

type MessageComposerContextType = {
    body: string;
    bodyLength: number;
    setBody: (body: string) => void;
    subjectLine: string;
    setSubjectLine: (subjectLine: string) => void;
    channelType: ChannelType;
    setChannelType: (channelType: ChannelType) => void;
    isGeneratingMessage: boolean;
    isSending: boolean;
    isSendable: boolean;
    disabledPrimaryActionReason: DisabledPrimaryActionReason | undefined;
    prospectId: string | undefined;
    messageType: MessageType;
    handleShowSuggestedMessage: () => void;
    suggestedDiffersFromDraft: boolean;
    handleChannelTypeChange: (channelType: ChannelType) => void;
    handleBodyTextChange: (body: string) => void;
    handleSubjectLineChange: (subjectLine: string) => void;
    setDraftMessage: (message: ComposerMessage) => void;
    removeDraftMessage: () => void;
    primaryAction: (options?: { omitMessage?: boolean }) => void;
    handleGenerateMessageAsEmail: () => void;
    regenerateMessage: (options?: { feedback?: string; path?: string }) => void;
};
const MessageComposerContextV2 = createContext<MessageComposerContextType | undefined>(undefined);

interface ValidationResult {
    valid: boolean;
    reason?: DisabledPrimaryActionReason;
}

interface ComposerMessage {
    content: string;
    subjectLine: string;
    channelType: ChannelType;
}

export function MessageComposerProviderV2({
    children,
    contactId,
    onMessageSent,
}: {
    children: React.ReactNode;
    contactId: string;
    onMessageSent?: () => void;
}) {
    const { data: contact } = useQuery(GET_CONTACT_BY_ID, {
        variables: { id: contactId },
        skip: !contactId,
    });
    const {
        linkedinConnections: { getConnectionStatus },
    } = useSdk();
    const { draftMessage, setDraftMessage, removeDraftMessage } = useDraftMessagesV2({ contactId });

    const [body, setBody] = useState('');
    const [subjectLine, setSubjectLine] = useState('');
    const [channelType, setChannelType] = useState<ChannelType>(ChannelType.LINKEDIN);
    const [completeAction] = useMutation(MARK_ACTION_AS_COMPLETED);

    const { data: connectionStatus } = useQueryTan(
        getConnectionStatus(contact?.Contacts_by_pk?.linkedinUserId, {
            enabled: !!contact?.Contacts_by_pk?.linkedinUserId,
        })
    );

    // Store latest values in a ref so cleanup function can access them
    const latestValues = useRef({
        body: '',
        subjectLine: '',
        channelType: ChannelType.LINKEDIN as ChannelType,
        draftMessage: null as typeof draftMessage,
    });

    // Update ref whenever values change
    useEffect(() => {
        latestValues.current = {
            body,
            subjectLine,
            channelType,
            draftMessage,
        };
    }, [body, subjectLine, channelType, draftMessage]);

    useEffect(() => {
        setBody(draftMessage?.content || '');
        setSubjectLine(draftMessage?.subjectLine || '');
        setChannelType(draftMessage?.channelType || ChannelType.LINKEDIN);
    }, [draftMessage]);

    const composerMessage: ComposerMessage = useMemo(() => {
        return {
            content: body,
            subjectLine,
            channelType,
        };
    }, [body, subjectLine, channelType]);

    const messageType = useMemo(() => {
        if (composerMessage.channelType === ChannelType.LINKEDIN) {
            return connectionStatus === ConnectionStatus.NOT_CONNECTED
                ? MessageType.linkedinConnectionRequest
                : MessageType.linkedinMessage;
        } else if (composerMessage.channelType === ChannelType.EMAIL) {
            return MessageType.emailMessage;
        } else if (composerMessage.channelType === ChannelType.NOTE) {
            return MessageType.note;
        } else {
        }
        return MessageType.linkedinMessage;
    }, [composerMessage.channelType, connectionStatus]);

    const { isSending, primaryAction, isGeneratingMessage, regenerateMessage, handleGenerateMessageAsEmail } =
        useMessageActions({
            contactId: contact?.Contacts_by_pk?.id,
            composerMessage,
            setBody,
            setSubjectLine,
            setChannelType,
            removeDraftMessage,
            setDraftMessage,
            messageType,
            onMessageSuccessfullySent: () => {
                completeAction({
                    variables: { contactId: contactId },
                    refetchQueries: [
                        GET_CONTACTS_WEIGHTED_SCORES,
                        { query: GET_CONTACT_BY_ID, variables: { id: contactId } },
                        {
                            query: GET_CONTACT_AND_CONVERSATION_BY_ID,
                            variables: { id: contactId, messageLimit: 20, noteLimit: 20 },
                        },
                    ],
                });
                onMessageSent?.();
            },
        });

    useAutoGenerateDraft({ contactId, regenerateMessage });

    const { isValidMessage, isValidRecipient, isValidConfiguration } = useMessageValidation(
        messageType,
        composerMessage,
        contactId,
        connectionStatus
    );

    const validationResult: ValidationResult = useMemo(() => {
        const configurationValidationResult = isValidConfiguration();
        const validMessage = isValidMessage();
        const validRecipient = isValidRecipient();

        if (!configurationValidationResult.valid) {
            return configurationValidationResult;
        }
        if (!validRecipient.valid) {
            return validRecipient;
        }
        if (!validMessage.valid) {
            return validMessage;
        }
        return { valid: true };
    }, [isValidMessage, isValidRecipient, isValidConfiguration]);

    const handleBodyTextChange = useCallback((value: string) => {
        setBody(value);
    }, []);

    const handleSubjectLineChange = useCallback((value: string) => {
        setSubjectLine(value);
    }, []);

    const handleChannelTypeChange = useCallback((channelType: ChannelType) => {
        setChannelType(channelType);
    }, []);

    // Save draft on unmount if changed
    useEffect(() => {
        return () => {
            const latest = latestValues.current;
            const hasChanged =
                latest.body !== latest.draftMessage?.content ||
                latest.subjectLine !== latest.draftMessage?.subjectLine ||
                latest.channelType !== latest.draftMessage?.channelType;

            // Only save if something changed and there's actual content
            if (hasChanged && channelType !== ChannelType.NOTE && (latest.body.trim() || latest.subjectLine.trim())) {
                setDraftMessage({
                    message: {
                        content: latest.body,
                        subjectLine: latest.subjectLine,
                        channelType: latest.channelType,
                    },
                    source: 'user',
                });
            }
        };
    }, []); // Empty dependency array is fine because we use refs

    return (
        <MessageComposerContextV2.Provider
            value={{
                body,
                bodyLength: body?.length || 0,
                setBody,
                subjectLine,
                setSubjectLine,
                channelType,
                setChannelType,
                isGeneratingMessage,
                isSending,
                isSendable: validationResult.valid,
                disabledPrimaryActionReason: validationResult?.reason,
                prospectId: contact?.Contacts_by_pk?.prospectId,
                messageType,
                handleShowSuggestedMessage: () => {},
                suggestedDiffersFromDraft: false,
                handleChannelTypeChange,
                handleBodyTextChange,
                handleSubjectLineChange,
                setDraftMessage: () => {},
                removeDraftMessage: () => {},
                handleGenerateMessageAsEmail,
                primaryAction,
                regenerateMessage,
            }}
        >
            {children}
        </MessageComposerContextV2.Provider>
    );
}

export function useMessageComposerV2() {
    const context = useContext(MessageComposerContextV2);
    if (context === undefined) {
        console.error('useMessageComposerV2 must be used within a MessageComposerProvider', new Error().stack);
    }
    return context;
}
